import React, { ReactNode, forwardRef, useMemo, HTMLAttributes, Ref } from 'react';
import { Link, LinkProps as RouterLinkProps } from 'react-router-dom';

import { ReactComponent as ArrowForwardIcon } from '../../../icons/arrowForward.svg';
import { StyledMenuItem, StyledForwardIcon } from './MenuItem.styles';

export interface MenuItemProps extends HTMLAttributes<HTMLDivElement> {
  /** The content of the MenuItem. */
  children: ReactNode;
  /** @ignore */
  disabled?: boolean;
  /** The url of the menu item, if it is a link. */
  url?: string;
  /** Indicated that the menu item is selected. */
  isSelected?: boolean;
  /** Indicated that the menu item is the ast om the list. */
  isLast?: boolean;
  /** Hide arrow next to the menu item. By default try for links but false for buttons. */
  showArrow?: boolean;
}

// eslint-disable-next-line react/prop-types
const RouterLink = forwardRef<HTMLAnchorElement, RouterLinkProps & { isSelected: boolean }>(({
  isSelected,
  ...props
}, ref) => <Link {...props} relative="path" ref={ref} />);

const MenuItem = forwardRef<
  HTMLAnchorElement | HTMLSpanElement | HTMLButtonElement,
  MenuItemProps
>(({ children, url, disabled, isLast, showArrow, onClick, ...props }, ref) => {
  const as = useMemo(() => {
    if (disabled) return 'span';
    if (url?.startsWith('http')) return 'a';
    if (url) return RouterLink;
    return 'button';
  }, [disabled, url]);

  const asProps = useMemo(() => {
    if (as === 'button') return { type: 'button' };
    if (as === 'a') return { href: url };
    if (as === RouterLink) return { to: url };
    return {};
  }, [as, url]);

  return (
    <StyledMenuItem<typeof as>
      role="menuitem"
      as={as}
      onMouseDown={disabled ? undefined : onClick}
      disabled={disabled}
      {...asProps}
      {...props}
      ref={ref as Ref<HTMLAnchorElement>}
    >
      {children}
      {(as !== 'button' || showArrow) && (
        <StyledForwardIcon
          as={ArrowForwardIcon}
        />
      )}
    </StyledMenuItem>
  );
});

export default MenuItem;
